// usage:
// v-tooltip="'autoTranslatedText'"
// v-tooltip="{content: 'autoTranslatedText'}"
// v-tooltip="{content: 'neverTranslatedText', autoTranslate:false}"
// v-tooltip="{content: 'forceTranslatedText', autoTranslate:true}"
// v-tooltip="{content: 'controlMyOffset', offset: [0, 16]}" // offset in px, see popper docs
// v-tooltip="{content: 'controlMyOffset', theme: 'lead'}" // sets theme class
// v-tooltip="{content: {type: 'image', url: '', height: '', width: ''}} // default image
// v-tooltip.bottom="'tooltip that tries to show on bottom if possible'"
// todo: nice to have: events, force show (programatic show), more themes

import useDom from "@/client/extensions/composition/useDom";
import {nextTick} from "vue";

let { isMobile } = useDom();

let tooltips = {};
let id = 0;

let popper = false;
let createPopper = false;
let animationClassIn = config.style.weUI.tooltip.animationClassIn;
let animationClassOut = config.style.weUI.tooltip.animationClassOut;
let extraClass = config.style.weUI.tooltip.extraClass;
let lazyLoadPopper = async () => {
  if (popper) {
    return popper;
  }

  popper = await import("@popperjs/core");
  createPopper = popper.createPopper;
  return true;
};

let getTooltipElement = (el, content, options) => {
  let safeContent = content.replace("<script>", "");
  let html = `<div class="tooltip saffron-tooltip ${extraClass} tooltip--${options.theme}"  role="tooltip" 
    data-tooltip-id="${options?.id}">
                            <div class="saffron-tooltip__content" role="tooltip-content">${safeContent}</div>
                            <div class="saffron-tooltip__arrow" role="tooltip-arrow"></div>
                          </div>`;
  let template = document.createElement("template");
  template.innerHTML = html.trim();
  return template.content.firstChild;
};

let getImageElement = (src, options) => {
  let safeSrc = src.replace("<script>", "");
  let alt = options.alt ? options.alt.replace("<script>", "") : "";
  let height = options.height ? options.height : 150;
  let width = options.width ? options.width : 150;
  let html = `<img src="${safeSrc}"
                            alt="${alt}"
                            style="max-height: ${height}px; max-width: ${width}px; height: auto;width:auto;object-fit: contain;">`;
  let template = document.createElement("template");
  template.innerHTML = html.trim();
  return template.content.firstChild.outerHTML;
};
let tooltipSymbol = Symbol("saffron-tooltip");
let tooltipWorkingSymbol = Symbol("saffron-tooltip-working");

let insertAfter = (targetNode, newNode) => {
  try {
    targetNode.parentNode.insertBefore(newNode, targetNode.nextSibling);
  } catch (e) {}
};

let showTooltip = (popper, instant = false) => {
  let el = popper.state.elements.popper;

  el.classList.remove(animationClassOut);
  el.style.display = "block";

  if (!instant) {
    el.classList.add(animationClassIn);
    el.classList.add("active");
  }

  popper.update();
};

let hideTooltip = (popper, instant = false) => {
  let el = popper.state.elements.popper;

  el.classList.remove(animationClassIn);
  el.classList.remove("active");
  if (instant) {
    el.style.display = "none";
  } else {
    el.classList.add(animationClassOut);
  }
  popper.update();
};

let toggleTooltip = (popper, instant = false) => {
  let el = popper.state.elements.popper;
  let isOpened = el.classList.contains("active");
  if (isOpened) {
    hideTooltip(popper, instant);
  } else {
    showTooltip(popper, instant);
  }
};

let bindPopper = async (options) => {
  if (utilities.isSSR()) {
    return;
  }

  options.id = id;
  id++;

  await lazyLoadPopper();

  let tooltipElement = getTooltipElement(options.el, options.content, options);

  // inject invisible tooltip element
  insertAfter(options.el, tooltipElement);
  // make it a popper and handlers
  let popper;

  try {
    popper = await createPopper(options.el, tooltipElement, {
      placement: options.placement,
      modifiers: [
        {
          name: "offset",
          options: {
            offset: options.offset,
          },
        },
        {
          name: "arrow",
          options: {
            element: '[role="tooltip-arrow"]',
            padding: options.arrowPadding ? options.arrowPadding : 0,
          },
        },
      ],
    });
  } catch (e) {
    try {
      popper = await createPopper(options.el, tooltipElement, {
        placement: options.placement,
        modifiers: [
          {
            name: "offset",
            options: {
              offset: options.offset,
            },
          },
          {
            name: "arrow",
            options: {
              element: '[role="tooltip-arrow"]',
              padding: options.arrowPadding ? options.arrowPadding : 0,
            },
          },
        ],
      });
    } catch (e) {}
  }
  if (!popper) {
    return false;
  }

  let showHandler = () => {
    if (!options.hide) {
      showTooltip(popper);
    }
  };
  let hideHandler = () => {
    hideTooltip(popper);
  };

  let toggleHandler = () => {
    toggleTooltip(popper);
  };
  // hide the tooltip right away
  hideTooltip(popper, true);

  // handlers to show and hide the tooltip further
  if (isMobile.value) {
    options.el.addEventListener("click", toggleHandler);
  } else {
    options.el.addEventListener("mouseenter", showHandler);
    options.el.addEventListener("focus", showHandler);
    options.el.addEventListener("mouseleave", hideHandler);
    options.el.addEventListener("blur", hideHandler);
  }

  // save references on element so that we can remove this later
  let toolTipRef = {
    popper: popper,
    tooltipElement: tooltipElement,
    showHandler: showHandler,
    hideHandler: hideHandler,
    options: options,
    id: options.id,
    oldId: false,
  };

  tooltips[id] = toolTipRef;

  if (options.el[tooltipSymbol]) {
    toolTipRef.oldId = options.el[tooltipSymbol].id;
  }

  options.el[tooltipSymbol] = toolTipRef;
  options.id = toolTipRef.id;
  options.oldId = toolTipRef.oldId;

  return { showHandler, hideHandler, popper };
};

let destroyTooltipByObject = (tooltipObject, options) => {
  let elements, el;
  if (!tooltipObject) {
    return true;
  }
  try {
    el = options.el;
    el.removeEventListener("mouseenter", tooltipObject.showHandler);
    el.removeEventListener("focus", tooltipObject.showHandler);
    el.removeEventListener("mouseleave", tooltipObject.hideHandler);
    el.removeEventListener("blur", tooltipObject.hideHandler);


    elements = tooltipObject?.popper?.state?.elements;
    tooltipObject.popper.destroy();
    tooltipObject.tooltipElement.remove();

    if (elements && typeof elements === "object") {
      for (let [key, element] of Object.entries(elements)) {
        if (key === "reference") {
          // reference is the original item from VUE that has the tooltip
          return;
        }
        element.css("border", "3px solid red!important");
        element.remove();
      }
    }
  } catch (e) {}
};

let unbindPopper = async (options) => {
  let tooltipObject;

  if (utilities.isSSR()) {
    return;
  }

  await lazyLoadPopper();

  try {
    tooltipObject = options.el[tooltipSymbol];
    if (tooltipObject) {
      destroyTooltipByObject(tooltipObject, options);
    }

    if (options.id) {
      tooltipObject = tooltips[options.id];
      destroyTooltipByObject(tooltipObject, options);
    }

    if (options.oldId) {
      tooltipObject = tooltips[options.oldId];
      destroyTooltipByObject(tooltipObject, options);
    }
  } catch (e) {}

  return true;
};

let getSafeOptions = (el, instance, value, modifiers) => {
  let safeOptions = {
    el,
    instance,
    value,
    modifiers,
    shouldApply: true,
  };

  if (!value) {
    return { ...safeOptions, shouldApply: false };
  }

  let rawValueOptions;

  if (value && typeof value === "object") {
    rawValueOptions = { ...value };
  } else {
    rawValueOptions = { content: value };
  }

  if (!rawValueOptions.hasOwnProperty("autoTranslate")) {
    rawValueOptions.autoTranslate = instance.autoTranslate;
  }

  if (!rawValueOptions.hasOwnProperty("translationParams")) {
    rawValueOptions.translationParams = {};
  }

  // content
  // string handling: auto translate content, if required (instance has auto translate, or we are instructed to auto translate
  if (
    rawValueOptions.autoTranslate &&
    typeof rawValueOptions.content === "string"
  ) {
    try {
      rawValueOptions.content = instance.translate(
        rawValueOptions.content,
        rawValueOptions.translationParams,
        true
      );
    } catch (e) {
      // exception can happen during development and block hmr
    }
  }

  // image handling
  if (
    rawValueOptions.content &&
    typeof rawValueOptions.content === "object" &&
    rawValueOptions.content.type === "image"
  ) {
    rawValueOptions.content = getImageElement(
      rawValueOptions.content.src,
      rawValueOptions.content
    );
  }

  if (!rawValueOptions.placement) {
    rawValueOptions.placement = "top";

    if (modifiers.left) {
      rawValueOptions.placement = "left";
    }
    if (modifiers.right) {
      rawValueOptions.placement = "right";
    }
    if (modifiers.bottom) {
      rawValueOptions.placement = "bottom";
    }
  }

  if (!rawValueOptions.offset) {
    rawValueOptions.offset = [0, 10];
  }

  if (!rawValueOptions.theme) {
    rawValueOptions.theme = "default";
  }

  safeOptions = { ...safeOptions, ...rawValueOptions };

  if (el[tooltipSymbol]) {
    safeOptions = {
      ...safeOptions,
      id: el[tooltipSymbol].options?.id,
      oldId: el[tooltipSymbol].options?.oldId,
    };
  }

  return safeOptions;
};

let resetTooltip = async (options, instance) => {
  if (utilities.isSSR()) {
    return;
  }

 nextTick(async() => {
   let isWorking = options.el[tooltipWorkingSymbol];
   if (isWorking) {
     return true;
   }

   if (!options.el || !options.el.isConnected) {
     return;
   }


   //tooltipWorkingSymbol
   options.el[tooltipWorkingSymbol] = true;
   await lazyLoadPopper();
   await unbindPopper(options);
   if (options.shouldApply && !options.hide) {
     await bindPopper(options);
   }
   options.el[tooltipWorkingSymbol] = false;
 });
};

export default {
  async created(el, { instance, value, modifiers }) {
    let options = getSafeOptions(el, instance, value, modifiers);
    // because popper uses time outs, it causes up problems. if we delay, it can help.
    resetTooltip(options, instance).catch((e) => {});
  },
  async updated(el, { instance, value, modifiers }) {
    let options = getSafeOptions(el, instance, value, modifiers);
    resetTooltip(options, instance);
  },
  async mounted(el, { instance, value, modifiers }) {
    if (utilities.isSSR()) {
      return;
    }
  },
  beforeUnmount(el, { instance, value, modifiers }) {
    if (utilities.isSSR()) {
      return;
    }

    try {
      if (instance?.saffronTooltipDebounce) {
        clearTimeout(instance.saffronTooltipDebounce);
      }
      let options = getSafeOptions(el, instance, value, modifiers);
      unbindPopper(options);
    } catch (e) {}
  },
};
